import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute - Wraps routes that require authentication
 * 
 * Props:
 * - children: The component to render if authenticated
 * - requireSubscription: If true, also checks for active subscription
 */
export default function ProtectedRoute({ children, requireSubscription = false, requireBetmaker = false, requireExactaValue = false }) {
  const { user, loading, hasActiveSubscription, hasBetmaker, hasExactaValue } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-emerald-400" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-slate-400 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Force password reset takes priority over everything else
  if (user.force_password_reset && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Check subscription if required
  if (requireSubscription && !hasActiveSubscription()) {
    return <Navigate to="/pricing" replace />;
  }

  // Check BetMaker access if required
  if (requireBetmaker && !hasBetmaker()) {
    return <Navigate to="/" replace />;
  }

  // Check Exacta Value access if required
  if (requireExactaValue && !hasExactaValue()) {
    return <Navigate to="/" replace />;
  }

  return children;
}
